// Push notifications in the iPhone app (brief 008). The website never calls these.
// The device's FCM token is saved in users/{uid}/devices/{token}, readable only by
// its owner; the sendPush Cloud Function sends every new in-app notification there.
import { doc, setDoc, deleteDoc, getDocs, collection, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { isNative } from './native';

let savedToken = null;

// Capacitor plugin objects answer to any method name, including `then`, so one must
// never be returned from an async function (awaiting it hangs). Destructure instead.
const loadPlugin = () => import('@capacitor-firebase/messaging');

const saveToken = async (uid, token) => {
  if (!token) return;
  savedToken = token;
  await setDoc(doc(db, 'users', uid, 'devices', token), { platform: 'ios', updatedAt: serverTimestamp() }, { merge: true });
};

// 'granted' | 'denied' | 'prompt' (not asked yet), or null on the website.
export const pushPermission = async () => {
  if (!isNative) return null;
  const { FirebaseMessaging } = await loadPlugin();
  const { receive } = await FirebaseMessaging.checkPermissions();
  return receive === 'granted' || receive === 'denied' ? receive : 'prompt';
};

// Ask iOS for permission (shows the system prompt once), then register.
export const enablePush = async (uid) => {
  const { FirebaseMessaging } = await loadPlugin();
  const { receive } = await FirebaseMessaging.requestPermissions();
  if (receive !== 'granted') return false;
  const { token } = await FirebaseMessaging.getToken();
  await saveToken(uid, token);
  return true;
};

// On sign-in: keep the token current and route taps. onOpen gets the push's data
// ({ notificationId, type, fromUid, fromName }). Returns a cleanup function.
export const startPush = async (uid, onOpen) => {
  if (!isNative) return () => {};
  const { FirebaseMessaging } = await loadPlugin();
  const handles = [
    await FirebaseMessaging.addListener('tokenReceived', ({ token }) => { saveToken(uid, token).catch(() => {}); }),
    await FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) => onOpen(notification?.data || {})),
  ];
  if ((await pushPermission()) === 'granted') {
    const { token } = await FirebaseMessaging.getToken();
    await saveToken(uid, token);
  }
  return () => handles.forEach(h => h.remove());
};

// On logout: this phone stops getting the signed-out user's pushes. The phone keeps
// its token (asking for a new one right after deleting it proved unreliable), and
// the next account to sign in on it registers the same token for itself.
export const stopPush = async (uid) => {
  if (!isNative || !savedToken) return;
  const token = savedToken;
  savedToken = null;
  await deleteDoc(doc(db, 'users', uid, 'devices', token)).catch(() => {});
};

// On account deletion: remove every device the user signed in on.
export const deleteAllDevices = async (uid) => {
  const snap = await getDocs(collection(db, 'users', uid, 'devices'));
  await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
};

// iOS Settings → MedXMeet, where notifications can be turned back on.
export const openAppSettings = () => { window.location.href = 'app-settings:'; };
